import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { API_URL } from '../utils/api';

// Stripe promise - will be initialized when we get the publishable key
let stripePromise = null;

// Card element styling
const cardStyle = {
  style: {
    base: {
      color: '#0d9488',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#94a3b8',
      },
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
};

// Payment Form Component
function PaymentForm({ onPaymentSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [amount, setAmount] = useState('10.00');
  const [description, setDescription] = useState('Test payment');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!stripe || !elements) {
      return;
    }

    const amountCents = Math.round(parseFloat(amount) * 100);
    if (isNaN(amountCents) || amountCents < 50) {
      setError('Amount must be at least $0.50');
      return;
    }

    setProcessing(true);

    try {
      // Create payment intent on backend
      const intentResponse = await fetch(`${API_URL}/stripe/test-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount_cents: amountCents,
          description,
        }),
      });

      const intentData = await intentResponse.json();

      if (!intentResponse.ok) {
        throw new Error(intentData.error || 'Failed to create payment intent');
      }

      // Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        intentData.client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (paymentIntent.status === 'succeeded') {
        setSuccess(`Payment successful! ID: ${paymentIntent.id}`);
        setAmount('10.00');
        setDescription('Test payment');
        elements.getElement(CardElement).clear();
        onPaymentSuccess();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <div className="form-group">
        <label htmlFor="amount">Amount (USD)</label>
        <input
          type="number"
          id="amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          min="0.50"
          step="0.01"
          placeholder="10.00"
        />
      </div>

      <div className="form-group">
        <label htmlFor="description">Description</label>
        <input
          type="text"
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Test payment"
        />
      </div>

      <div className="form-group">
        <label>Card Details</label>
        <div className="card-element-container">
          <CardElement options={cardStyle} />
        </div>
      </div>

      {error && <div className="payment-error">{error}</div>}
      {success && <div className="payment-success">{success}</div>}

      <button
        type="submit"
        disabled={!stripe || processing}
        className="pay-button"
      >
        {processing ? 'Processing...' : `Pay $${amount}`}
      </button>

      <div className="test-cards-info">
        <h4>Test Card Numbers</h4>
        <ul>
          <li><code>4242 4242 4242 4242</code> - Successful payment</li>
          <li><code>4000 0000 0000 3220</code> - 3D Secure authentication</li>
          <li><code>4000 0000 0000 9995</code> - Declined (insufficient funds)</li>
          <li><code>4000 0000 0000 0002</code> - Declined (generic)</li>
        </ul>
        <p>Use any future expiry date, any 3-digit CVC, and any ZIP code.</p>
      </div>
    </form>
  );
}

// Payment Activity List Component
function PaymentActivity({ payments, loading, onRefresh }) {
  const formatAmount = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'succeeded':
        return 'status-succeeded';
      case 'processing':
        return 'status-processing';
      case 'requires_payment_method':
      case 'requires_confirmation':
      case 'requires_action':
        return 'status-pending';
      case 'canceled':
        return 'status-canceled';
      default:
        return 'status-default';
    }
  };

  return (
    <div className="payment-activity">
      <div className="activity-header">
        <h3>Recent Payment Activity</h3>
        <button onClick={onRefresh} className="refresh-button" disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {loading && payments.length === 0 ? (
        <div className="loading-state">Loading payments...</div>
      ) : payments.length === 0 ? (
        <div className="empty-state">No payments yet. Make a test payment above!</div>
      ) : (
        <div className="payments-list">
          {payments.map((payment) => (
            <div key={payment.id} className="payment-item">
              <div className="payment-main">
                <div className="payment-amount">{formatAmount(payment.amount)}</div>
                <div className={`payment-status ${getStatusClass(payment.status)}`}>
                  {payment.status}
                </div>
              </div>
              <div className="payment-details">
                <span className="payment-description">
                  {payment.description || 'No description'}
                </span>
                <span className="payment-date">{formatDate(payment.created)}</span>
              </div>
              <div className="payment-id">
                <code>{payment.id}</code>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Main Test Payment Page
function TestPayment() {
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(true);

  // Load Stripe config and initialize
  useEffect(() => {
    const loadStripeConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/stripe/config`);
        const data = await response.json();

        if (!data.publishableKey) {
          throw new Error('Stripe publishable key not configured. Check your .env file.');
        }

        stripePromise = loadStripe(data.publishableKey);
        setStripeLoaded(true);
      } catch (err) {
        setConfigError(err.message);
      }
    };

    loadStripeConfig();
  }, []);

  // Load recent payments
  const loadPayments = async () => {
    setLoadingPayments(true);
    try {
      const response = await fetch(`${API_URL}/stripe/recent-payments`);
      const data = await response.json();
      setPayments(data);
    } catch (err) {
      console.error('Failed to load payments:', err);
    } finally {
      setLoadingPayments(false);
    }
  };

  useEffect(() => {
    if (stripeLoaded) {
      loadPayments();
    }
  }, [stripeLoaded]);

  if (configError) {
    return (
      <div className="test-payment-page">
        <div className="page-header">
          <h1>Stripe Test Payments</h1>
        </div>
        <div className="config-error">
          <h3>Configuration Error</h3>
          <p>{configError}</p>
          <p>Make sure you have set <code>STRIPE_PUBLISHABLE_KEY</code> in your backend .env file.</p>
        </div>
      </div>
    );
  }

  if (!stripeLoaded) {
    return (
      <div className="test-payment-page">
        <div className="page-header">
          <h1>Stripe Test Payments</h1>
        </div>
        <div className="loading-state">Loading Stripe...</div>
      </div>
    );
  }

  return (
    <div className="test-payment-page">
      <div className="page-header">
        <h1>Stripe Test Payments</h1>
        <p className="subtitle">Test your Stripe integration with test card numbers</p>
      </div>

      <div className="test-payment-content">
        <div className="payment-section">
          <h2>Make a Test Payment</h2>
          <Elements stripe={stripePromise}>
            <PaymentForm onPaymentSuccess={loadPayments} />
          </Elements>
        </div>

        <PaymentActivity
          payments={payments}
          loading={loadingPayments}
          onRefresh={loadPayments}
        />
      </div>
    </div>
  );
}

export default TestPayment;
